import {
  type ChangeKind,
  changeKindOf,
  readableText,
  type WikiChangeItem,
  type WikiReviewItem,
  type WikiThread,
} from "@esi/shared";
import {
  Check,
  ChevronRight,
  CircleQuestionMark,
  MessageSquare,
  MessageSquareWarning,
  PanelRight,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, type RefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PassageAnchor } from "./review-anchors.ts";
import { rangeOf } from "./selection.ts";

// The review in the reading view (wiki-collaboration-ux D6, D7, D14, D15; mock
// `.checkpoints/s3-redesign-2/`): calm text with count markers, blocking items as one amber line
// under their passage, the item itself in the companion pane, and the decision bar.

/** The strip's selection for the structure items, shown together as one tree. */
export const STRUCTURE = "structure";

export type OutlineGroup = "content" | "structure" | "removals";
const GROUP_OF: Record<ChangeKind, OutlineGroup> = {
  created: "content",
  edits: "content",
  renames: "content",
  moves: "structure",
  folders: "structure",
  deletions: "removals",
};
export const outlineGroupOf = (item: Pick<WikiChangeItem, "op">) => GROUP_OF[changeKindOf(item.op)];

/** A possible contradiction as the pane shows it: the flagged sentence and the source's excerpt. */
export interface Flag {
  id: string;
  statement: string;
  explanation: string;
}

/**
 * The detail of a contradiction flag is "<statement> — <explanation>" (both written by core, and
 * either may hold a spaced dash). With the page's Markdown, the split whose statement reads on the
 * page wins; without it, or when none does, the last dash (a statement more often holds one).
 */
export function flagOf(item: WikiReviewItem, page?: string | null): Flag {
  const detail = item.detail ?? "";
  const splits: number[] = [];
  for (let at = detail.indexOf(" — "); at >= 0; at = detail.indexOf(" — ", at + 1)) {
    splits.push(at);
  }
  if (!splits.length) {
    return { id: item.id, statement: "", explanation: detail };
  }
  const squeeze = (text: string) => text.normalize("NFC").replace(/\s+/g, "");
  const text = page ? squeeze(readableText(page)) : "";
  const onPage = text ? splits.find((s) => text.includes(squeeze(detail.slice(0, s)))) : undefined;
  const at = onPage ?? (splits.at(-1) as number);
  return { id: item.id, statement: detail.slice(0, at), explanation: detail.slice(at + 3) };
}

export type ItemDecision = "approve" | "reject" | "reopen";

/**
 * One review item as the companion pane holds it (D15): a thread on this page, or a possible
 * contradiction. An open change request and a contradiction block publishing (or ask for a
 * decision): they are the page's open items, the amber lines and the stepper's steps.
 */
export type ReviewEntry =
  | { key: string; type: "thread"; thread: WikiThread; blocking: boolean }
  | { key: string; type: "flag"; flag: Flag; source: { id: string; title: string } | null };

export const entryOfThread = (thread: WikiThread): ReviewEntry => ({
  key: thread.id,
  type: "thread",
  thread,
  blocking: thread.kind === "change_request" && thread.status === "open",
});

export const entryOfFlag = (item: WikiReviewItem, page?: string | null): ReviewEntry => ({
  key: item.id,
  type: "flag",
  flag: flagOf(item, page),
  source: item.source ?? null,
});

export const isBlocking = (entry: ReviewEntry) => entry.type === "flag" || entry.blocking;

/** The passage an entry sits on: the thread's anchor, or the flagged sentence. */
export function anchorOf(entry: ReviewEntry): PassageAnchor | null {
  if (entry.type === "flag") {
    return entry.flag.statement ? { quote: entry.flag.statement, prefix: "", suffix: "" } : null;
  }
  const anchor = entry.thread.anchor;
  return anchor?.quote
    ? { quote: anchor.quote, prefix: anchor.prefix ?? "", suffix: anchor.suffix ?? "" }
    : null;
}

/** Comments that are not the agent's system notes, the thread's opening one included. */
const repliesOf = (thread: WikiThread) =>
  Math.max(0, thread.comments.filter((c) => !c.system).length - 1);

/**
 * A blocking item as one amber line under its passage (D15): the kind, who and the replies, then
 * Open. A button: it opens the item in the pane and moves focus there.
 */
export function IssueLine(props: { entry: ReviewEntry; open: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  const { entry } = props;
  const Icon = entry.type === "flag" ? TriangleAlert : MessageSquareWarning;
  const title =
    entry.type === "flag" ? t("wiki.review.line.contradiction") : t("wiki.review.line.request");
  const detail =
    entry.type === "flag"
      ? entry.source
        ? t("wiki.review.line.source", { title: entry.source.title })
        : entry.flag.explanation
      : t("wiki.review.line.who", {
          name: entry.thread.createdByName ?? t("wiki.threads.someone"),
          count: repliesOf(entry.thread),
        });
  return (
    <button
      type="button"
      data-issue-line={entry.key}
      aria-expanded={props.open}
      onClick={props.onOpen}
      className={cn(
        "flex h-8 w-full min-w-0 items-center gap-2 rounded-md border px-2.5 text-left text-[13px] transition-colors duration-(--duration-fast) focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        props.open
          ? "border-line-strong bg-surface-selected text-fg"
          : "border-warning-border/40 bg-warning-bg text-warning-fg hover:border-warning-border",
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      <span className="shrink-0 font-medium">{title}</span>
      <span className="min-w-0 truncate text-fg-secondary">· {detail}</span>
      <span
        className={cn(
          "ml-auto flex shrink-0 items-center gap-1 text-xs",
          props.open ? "font-medium text-accent-text" : "text-fg-secondary",
        )}
      >
        {props.open ? t("wiki.review.line.opened") : t("wiki.review.line.open")}
        {props.open ? (
          <PanelRight aria-hidden="true" className="size-3" />
        ) : (
          <ChevronRight aria-hidden="true" className="size-3" />
        )}
      </span>
    </button>
  );
}

/** The lines under one passage, stacked (a passage may carry a change request and a flag). */
export function IssueLines(props: { children: ReactNode }) {
  return (
    <div
      data-selection-ignore=""
      data-issue-lines=""
      data-esi-content="wiki"
      className="-mt-1.5 flex flex-col gap-1"
    >
      {props.children}
    </div>
  );
}

/**
 * A count marker at the end of a sentence (D15): a question or a comment (its comment count), or
 * a blocking item folded by the Show issues inline switch (amber).
 */
export function IssueMarker(props: { entry: ReviewEntry; open: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  const { entry } = props;
  const amber = isBlocking(entry);
  const Icon =
    entry.type === "flag"
      ? TriangleAlert
      : entry.thread.kind === "change_request"
        ? MessageSquareWarning
        : entry.thread.kind === "question"
          ? CircleQuestionMark
          : MessageSquare;
  const count = entry.type === "thread" ? entry.thread.comments.filter((c) => !c.system).length : 0;
  const label =
    entry.type === "flag"
      ? t("wiki.review.marker.flag")
      : t("wiki.review.marker.thread", {
          kind: t(`wiki.threads.kind.${entry.thread.kind}`),
          name: entry.thread.createdByName ?? t("wiki.threads.someone"),
          count,
        });
  return (
    <button
      type="button"
      data-selection-ignore=""
      data-issue-marker={entry.key}
      aria-label={label}
      aria-expanded={props.open}
      onClick={props.onOpen}
      className={cn(
        "relative ml-1 inline-flex h-5 items-center gap-[3px] rounded-full border px-1.5 align-[2px] text-[11px] leading-none font-semibold tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        amber
          ? "border-warning-border/45 bg-warning-bg text-warning-fg"
          : "border-line bg-surface-raised text-fg-secondary hover:text-fg",
        props.open && "outline-2 outline-offset-1 outline-accent-text",
      )}
    >
      <Icon aria-hidden="true" className="size-3" strokeWidth={2} />
      {count ? <span aria-hidden="true">{count}</span> : null}
    </button>
  );
}

/**
 * Rings the open item's passage (D15) and tints every passage with a thread or a flag: CSS Custom
 * Highlights where the browser has them, nothing changed otherwise.
 */
export function usePassageHighlights(
  container: RefObject<HTMLElement | null>,
  passages: { threads: readonly PassageAnchor[]; flags: readonly PassageAnchor[] },
  open: PassageAnchor | null,
  version: unknown,
) {
  const key = JSON.stringify([passages, open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `version` re-finds a new render's ranges.
  useEffect(() => {
    const root = container.current;
    const registry = (globalThis.CSS as { highlights?: Map<string, unknown> } | undefined)
      ?.highlights;
    const Highlight = (globalThis as { Highlight?: new (...ranges: Range[]) => unknown }).Highlight;
    if (!root || !registry || !Highlight) {
      return;
    }
    const [lists, ring] = JSON.parse(key) as [
      { threads: PassageAnchor[]; flags: PassageAnchor[] },
      PassageAnchor | null,
    ];
    const ranges = (list: readonly PassageAnchor[]) =>
      list.flatMap((p) => {
        const range = p.quote ? rangeOf(root, p.quote, p) : null;
        return range ? [range] : [];
      });
    registry.set("esi-thread", new Highlight(...ranges(lists.threads)));
    registry.set("esi-flag", new Highlight(...ranges(lists.flags)));
    registry.set("esi-open", new Highlight(...ranges(ring ? [ring] : [])));
    return () => {
      registry.delete("esi-thread");
      registry.delete("esi-flag");
      registry.delete("esi-open");
    };
  }, [container, key, version]);
}

/** Scrolls the page back to a passage (Jump to the passage); false when it is not on the page. */
export function jumpTo(container: HTMLElement | null, anchor: PassageAnchor | null): boolean {
  const range = container && anchor ? rangeOf(container, anchor.quote, anchor) : null;
  const target = range?.startContainer.parentElement;
  if (!target) {
    return false;
  }
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  return true;
}

/** The decision bar (D6): Request changes and the ink action, sticky at the bottom. */
export function DecisionBar(props: {
  status: string | null;
  /** Request changes: `wiki.draft` on an open set. */
  request: boolean;
  /** "publish": Approve and publish; "approve": Approve (someone else publishes); none. */
  ink: "publish" | "approve" | null;
  inkDisabled: boolean;
  inkDescribedBy?: string | undefined;
  busy: boolean;
  onRequest: () => void;
  onInk: () => void;
  /** In the bottom sheet's footer (D15) rather than under the page column. */
  inSheet?: boolean;
  /** Before the status text: the agent revision line (wiki-resolve-with-agent D7). */
  lead?: ReactNode;
  /** The overflow menu before the actions: Resolve answered (wiki-resolve-with-agent D5). */
  menu?: ReactNode;
}) {
  const { t } = useTranslation();
  if (!props.request && !props.ink && !props.menu) {
    return null;
  }
  return (
    <section
      data-decision-bar=""
      aria-label={t("wiki.review.bar.label")}
      className={cn(
        "flex flex-wrap items-center gap-2 bg-surface-raised",
        props.inSheet
          ? "px-4 pt-2 pb-[max(0.625rem,env(safe-area-inset-bottom))]"
          : "shrink-0 border-t border-line px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-6",
      )}
    >
      {props.status || props.lead ? (
        <p className="flex min-w-0 flex-1 basis-full flex-wrap items-center gap-x-1.5 text-xs text-fg-secondary sm:basis-auto">
          {props.lead}
          {props.lead && props.status ? <span aria-hidden="true">·</span> : null}
          {props.status ? <span>{props.status}</span> : null}
        </p>
      ) : (
        <span className="hidden flex-1 sm:block" />
      )}
      <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto sm:flex-nowrap">
        {props.menu}
        {props.request ? (
          <Button
            variant="outline"
            className="flex-1 sm:flex-none"
            disabled={props.busy}
            onClick={props.onRequest}
          >
            {t("wiki.review.bar.request")}
          </Button>
        ) : null}
        {props.ink ? (
          <Button
            className="flex-1 sm:flex-none"
            disabled={props.busy || props.inkDisabled}
            aria-describedby={props.inkDescribedBy}
            onClick={props.onInk}
          >
            <Check aria-hidden="true" />
            {props.ink === "publish"
              ? t("wiki.review.bar.approvePublish")
              : t("wiki.review.bar.approve")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** An item the reading view renders as a page with marks: a new page or an edit. */
export function isContentReadable(item: Pick<WikiChangeItem, "op">) {
  return item.op === "create" || item.op === "edit";
}
